/**
 * Group-thread shared-store READ (group-threading P3c, Option B).
 *
 * The server (P3b) mirrors every group-thread turn into
 *   group_thread_messages/{threadId}/messages/{messageId}
 * and firestore.rules (P3c) admit only that thread's participants. This module
 * is the client half: fetch the shared transcript as an OVERLAY and union it
 * into the own-tree read, because under Option B neither store is complete on
 * its own:
 *   - the OWNER's tree never receives OTHER participants' human turns (the
 *     server writes those only to the shared store);
 *   - a NON-OWNER participant's tree starts empty and only accumulates what
 *     their own client mirrors.
 *
 * Merge contract (pure, tested):
 *   - union keyed by message id — OWN entries always win (richer local shape:
 *     upgraded attachments, live finalize state) — EXCEPT for the one field
 *     the two stores disagree on by design: when the overlay copy's
 *     `attachmentsSerialized` is locator-richer, it is grafted onto the own
 *     copy (2026-09-07 owner report: a photo sent to another user vanished
 *     when coming back — the recipient had already persisted a TEXT-ONLY copy
 *     of the sender's turn under the shared doc's id, so the storage-backed
 *     attachment the server wrote later could never heal it). Same
 *     never-downgrade rule as the dual-store merge (types/attachmentRichness).
 *   - overlay copies of the READER's own human turns are dropped whenever the
 *     own list is non-empty: the reader's uuid-keyed bubble and the server's
 *     `prest0n_task_<id>__user` doc are the same turn under two ids, and
 *     keeping both would double the author's question bubble. (With an empty
 *     own list — fresh device of a participant who never posted — nothing can
 *     dup, so everything is kept.)
 *   - stable chronological sort; ties keep own-then-overlay insertion order
 *     (the overlay is pre-sorted orderTs→seq, so a question stays above its
 *     answer).
 *
 * Fail-closed: flag off → null before any Firestore access; rules-denied /
 * offline / malformed → null. A null overlay leaves every existing read
 * byte-identical to today.
 */
import {
  collection, getDocs, getFirestore, limit, onSnapshot, query, where,
  type QueryConstraint,
} from 'firebase/firestore';
import type { ChatMessage } from '../components/GlobalChatSystem/types';
import {
  fromGroupThreadMessageDocs,
  type GroupThreadMessageDoc,
} from '../types/groupThreadMessageDoc';
import { resolveGroupThreadReadEnabled } from '../services/groupThreads/featureFlags';
import { getGroupThreadHistoryFloor } from '../services/groupThreads/historyFloors';
import { attachmentLocatorRichness, graftRicherAttachments } from '../types/attachmentRichness';
import { canonicalGroupOrderTs } from '../types/taskAnchor';

const GROUP_MESSAGES_COLLECTION = 'group_thread_messages';

/**
 * The shared-store query for one thread, floored at the caller's join point
 * (GT-01).
 *
 * Every read of this store goes through here so the `orderTs >= floor`
 * constraint cannot be forgotten at one call site. It is not merely an
 * optimisation: firestore.rules deny each message below the caller's floor, and
 * a Firestore `list` fails WHOLESALE when any candidate document is denied. An
 * unfiltered query on a thread with pre-join messages is therefore a hard
 * permission-denied for the entire transcript rather than a truncation — the
 * late joiner would see nothing at all, including their own messages. Filtering
 * keeps the candidate set inside what the rules allow, so the query succeeds and
 * simply returns less.
 *
 * Fail-closed: an unpublished floor resolves to MAX_SAFE_INTEGER, which returns
 * an empty set rather than the whole transcript.
 */
function flooredThreadMessagesQuery(threadId: string, ...extra: QueryConstraint[]) {
  const floor = getGroupThreadHistoryFloor(threadId);
  return query(
    collection(getFirestore(), GROUP_MESSAGES_COLLECTION, threadId, 'messages'),
    where('orderTs', '>=', floor),
    ...extra,
  );
}

/** Shared transcript for a thread, or null when flag-off / denied / absent. */
export async function getGroupThreadOverlay(threadId: string): Promise<ChatMessage[] | null> {
  if (!resolveGroupThreadReadEnabled()) return null;
  const id = (threadId || '').trim();
  if (!id) return null;
  try {
    const snap = await getDocs(flooredThreadMessagesQuery(id));
    if (snap.empty) return null;
    const docs = snap.docs.map((d) => d.data() as GroupThreadMessageDoc);
    const revived = fromGroupThreadMessageDocs(docs);
    return revived.length > 0 ? revived : null;
  } catch {
    return null; // non-participant (rules deny), offline, or store absent — inert
  }
}

/**
 * LIVE shared transcript for a thread (group-threading delivery fix,
 * 2026-07-23). The one-shot overlay above only runs inside a thread-open read,
 * so a participant sitting in an open thread never saw turns the OTHER
 * participant sent after that read — the mirror wrote them to the shared store
 * but nothing on the recipient's device ever looked again (verified live:
 * owner turn 12:54Z mirrored, member view stopped at 11:44Z). This subscribes
 * to the store while a thread is open and hands each snapshot to the caller,
 * which merges it through the SAME `mergeOwnAndGroupMessages` contract.
 *
 * Fail-closed like every group read: flag off → no Firestore access and a
 * no-op detach; a listener error backs off and RE-ATTACHES (2026-08-26 —
 * "up to date in real time … no lag whatsoever"). The original version
 * detached permanently and silently on ANY error, so one transient network
 * blip or a rules-propagation race (a just-added member whose membership
 * write was still settling) killed live delivery for the rest of that thread
 * view — the exact quiet-lag class the directive is about. A genuine
 * non-participant still ends up inert: every retry is denied the same way,
 * and the schedule stops growing at a slow steady interval.
 */
export const GROUP_LISTENER_RETRY_DELAYS_MS: readonly number[] = [1_000, 5_000, 15_000, 30_000];

export function subscribeGroupThreadMessages(
  threadId: string,
  onMessages: (messages: ChatMessage[]) => void,
  opts?: {
    /**
     * Resolve the account-scoped flag against THIS account instead of the
     * mount-published one. The publish (`setGroupThreadReadAccount`) happens in
     * a GlobalChatSystem effect only after the async account resolution — a
     * subscribe fired at page load for the RESTORED active thread ran before
     * it, read the flag as OFF, and returned a permanent no-op (verified live
     * 2026-07-23: the owner's restored-thread view never received the member's
     * store-only turns, while the member — who clicks into the thread after
     * resolution — got everything). Callers pass the resolved account and
     * re-invoke when it changes.
     */
    accountId?: string | null;
  },
): () => void {
  const flagCtx = opts?.accountId ? { accountId: opts.accountId } : {};
  if (!resolveGroupThreadReadEnabled(flagCtx)) return () => {};
  const id = (threadId || '').trim();
  if (!id) return () => {};

  let stopped = false;
  let detachInner: (() => void) | null = null;
  // Environment-agnostic timer handle: this module also runs under Node-env
  // tests where `window` does not exist.
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let retryIndex = 0;

  const attach = (): void => {
    if (stopped) return;
    try {
      // The floor is read at every (re)attach, so a floor that resolved after
      // the first attempt (the membership read landing) is picked up by the
      // retry instead of being baked in stale.
      const unsubscribe = onSnapshot(
        flooredThreadMessagesQuery(id),
        (snap) => {
          retryIndex = 0; // a delivered snapshot proves the channel — reset
          if (snap.empty) return;
          const docs = snap.docs.map((d) => d.data() as GroupThreadMessageDoc);
          const revived = fromGroupThreadMessageDocs(docs);
          if (revived.length > 0) onMessages(revived);
        },
        () => {
          try { unsubscribe(); } catch { /* already detached */ }
          if (stopped) return;
          const delay = GROUP_LISTENER_RETRY_DELAYS_MS[
            Math.min(retryIndex, GROUP_LISTENER_RETRY_DELAYS_MS.length - 1)
          ];
          retryIndex += 1;
          retryTimer = setTimeout(attach, delay);
        },
      );
      detachInner = unsubscribe;
    } catch {
      // Synchronous listener construction failure — same backoff as an error.
      if (stopped) return;
      const delay = GROUP_LISTENER_RETRY_DELAYS_MS[
        Math.min(retryIndex, GROUP_LISTENER_RETRY_DELAYS_MS.length - 1)
      ];
      retryIndex += 1;
      retryTimer = setTimeout(attach, delay);
    }
  };

  attach();

  return () => {
    stopped = true;
    if (retryTimer !== null) clearTimeout(retryTimer);
    try { detachInner?.(); } catch { /* already detached */ }
  };
}

/** limit(1) probe: does the shared store have ANY message for this thread? */
export async function hasAnyGroupThreadMessages(threadId: string): Promise<boolean> {
  if (!resolveGroupThreadReadEnabled()) return false;
  const id = (threadId || '').trim();
  if (!id) return false;
  try {
    const snap = await getDocs(flooredThreadMessagesQuery(id, limit(1)));
    return !snap.empty;
  } catch {
    return false;
  }
}

function messageKey(m: ChatMessage): string {
  const anchor = typeof m.taskAnchorId === 'string' ? m.taskAnchorId.trim() : '';
  const id = typeof m.id === 'string' ? m.id.trim() : '';
  return id || anchor;
}

/**
 * Transcript order: the shared-store key when both messages carry one, else the
 * timestamp. Within one turn `orderTs` ties and `seq` decides (question 0,
 * answer 1). A message with no `orderTs` — a solo/local bubble — is compared on
 * its timestamp, so mixed lists still order sensibly.
 */
export function compareForTranscript(a: ChatMessage, b: ChatMessage): number {
  // CANONICAL position, not the stored stamp (2026-09-09): a turn's two docs
  // are written by more than one writer, and a clock difference between them
  // put the answer above its own question for every participant but the asker.
  // Derived per-document from the doc's own id, so this stays a valid total
  // order — a pairwise "same turn ⇒ compare by seq" rule is not transitive.
  // Only reached when BOTH sides carry a stored orderTs (i.e. shared-store
  // docs); a local bubble still falls through to the timestamp branch below.
  const ao = typeof a?.orderTs === 'number' ? canonicalGroupOrderTs(a) : undefined;
  const bo = typeof b?.orderTs === 'number' ? canonicalGroupOrderTs(b) : undefined;
  if (ao !== undefined && bo !== undefined) {
    if (ao !== bo) return ao - bo;
    const as = typeof a?.seq === 'number' ? a.seq : 0;
    const bs = typeof b?.seq === 'number' ? b.seq : 0;
    if (as !== bs) return as - bs;
    return 0;
  }
  return timeOf(a) - timeOf(b);
}

function timeOf(m: ChatMessage): number {
  const t = m?.timestamp instanceof Date ? m.timestamp.getTime() : new Date(m?.timestamp as unknown as string).getTime();
  return Number.isFinite(t) ? t : 0;
}

/**
 * Union the own-tree read with the shared-store overlay. See module doc for
 * the full contract. `currentUid` identifies the reader so their own human
 * turns from the overlay can be dropped (dup prevention).
 */
/** How far apart (ms) an own bubble's instant and the store's echo may sit and still be the same turn. */
export const OWN_COPY_HEAL_WINDOW_MS = 120_000;

function localInstant(m: ChatMessage): number | null {
  if (typeof m.orderTs === 'number' && Number.isFinite(m.orderTs)) return m.orderTs;
  const t = m.timestamp as unknown;
  const ms = t instanceof Date ? t.getTime() : new Date(t as string).getTime();
  return Number.isFinite(ms) && ms > 0 ? ms : null;
}

/**
 * The index of the reader's own local bubble that the store echo `echo` is a
 * copy of, or null: an own user turn with NO shared id yet, the same text,
 * within the heal window — the closest in time when several qualify.
 */
export function findOwnCopyIndex(list: ReadonlyArray<ChatMessage>, echo: ChatMessage, uid: string): number | null {
  const text = typeof echo.text === 'string' ? echo.text.trim() : '';
  if (!text) return null;
  const echoTs = localInstant(echo);
  let bestIdx = -1;
  let bestGap = Number.POSITIVE_INFINITY;
  for (let idx = 0; idx < list.length; idx += 1) {
    const m = list[idx];
    if (m.isUser !== true) continue;
    if (typeof m.sharedMessageId === 'string' && m.sharedMessageId.trim()) continue;
    const sender = typeof m.senderId === 'string' ? m.senderId.trim() : '';
    if (sender && sender !== uid) continue;
    if ((typeof m.text === 'string' ? m.text.trim() : '') !== text) continue;
    const ts = localInstant(m);
    const gap = echoTs !== null && ts !== null ? Math.abs(echoTs - ts) : 0;
    if (gap > OWN_COPY_HEAL_WINDOW_MS) continue;
    if (gap < bestGap) { bestIdx = idx; bestGap = gap; }
  }
  return bestIdx >= 0 ? bestIdx : null;
}

/** The local copy with the echo's shared id + instant grafted on (the copy's own fields win everywhere else). */
export function stampSharedIdentity(local: ChatMessage, echo: ChatMessage, sharedId: string): ChatMessage {
  const out: ChatMessage = { ...local, sharedMessageId: sharedId };
  if (typeof echo.orderTs === 'number' && Number.isFinite(echo.orderTs)) out.orderTs = echo.orderTs;
  if (typeof echo.seq === 'number' && Number.isFinite(echo.seq)) out.seq = echo.seq;
  return out;
}

export function mergeOwnAndGroupMessages(
  own: ChatMessage[],
  overlay: ChatMessage[],
  currentUid: string,
): ChatMessage[] {
  const ownList = Array.isArray(own) ? own : [];
  const overlayList = Array.isArray(overlay) ? overlay : [];
  if (overlayList.length === 0) return ownList;

  const uid = (currentUid || '').trim();
  const seen = new Set<string>();
  // key → index into ownList, so a colliding overlay copy can heal the own
  // copy's attachment set in place (see the module doc's merge contract).
  const ownIndexByKey = new Map<string, number>();
  ownList.forEach((m, idx) => {
    const key = messageKey(m);
    if (key) {
      seen.add(key);
      if (!ownIndexByKey.has(key)) ownIndexByKey.set(key, idx);
    }
    // Anchor alias: an own assistant doc keyed by uuid may still carry the
    // task anchor; index it too so the overlay's anchored copy collapses.
    const anchor = typeof m.taskAnchorId === 'string' ? m.taskAnchorId.trim() : '';
    if (anchor) {
      seen.add(anchor);
      if (!ownIndexByKey.has(anchor)) ownIndexByKey.set(anchor, idx);
    }
  });

  // ATTACHMENT HEAL (2026-09-07 owner report): the own copy wins the
  // collision, but never at the cost of a poorer attachment set. Copy-on-
  // write: `healed` is materialised only when a graft actually lands, so the
  // no-change path still returns the SAME `ownList` reference (the caller's
  // identity exit depends on it).
  let healed: ChatMessage[] | null = null;
  const additions: ChatMessage[] = [];
  for (const m of overlayList) {
    const key = messageKey(m);
    if (key && seen.has(key)) {
      // own copy wins — graft the overlay's richer locators onto it
      const idx = ownIndexByKey.get(key);
      if (idx !== undefined) {
        const current = (healed ?? ownList)[idx];
        if (attachmentLocatorRichness(m) > attachmentLocatorRichness(current)) {
          if (!healed) healed = ownList.slice();
          healed[idx] = graftRicherAttachments(current, m);
        }
      }
      continue;
    }
    if (
      ownList.length > 0 &&
      m.isUser === true &&
      typeof m.senderId === 'string' &&
      m.senderId.trim() !== '' &&
      m.senderId.trim() === uid
    ) {
      // OWN-COPY HEAL (2026-09-09): the reader's own question is represented
      // locally under another id. Before dropping the echo, stamp its shared
      // identity (the store id + instant) onto that local copy when the copy
      // has none — turns sent before the shared id was minted client-side —
      // so reactions, delivery and search can key on it. Copy-on-write, like
      // the attachment heal above.
      const idx = findOwnCopyIndex(healed ?? ownList, m, uid);
      if (idx !== null && key) {
        const current = (healed ?? ownList)[idx];
        if (!healed) healed = ownList.slice();
        // PR-6 (2026-09-09): the echo may carry the locators the local copy
        // never got — the upload finished after the bubble was written, or on
        // another device — so the richer attachment set rides along through
        // the same never-downgrade rule as the keyed collision above.
        healed[idx] = graftRicherAttachments(stampSharedIdentity(current, m, key), m);
      }
      continue; // reader's own question — locally represented under another id
    }
    if (key) seen.add(key);
    additions.push(m);
  }
  // Heal-only: own order is preserved exactly (nothing new to sort in);
  // the fresh array signals the change to the caller's identity exit.
  if (additions.length === 0) return healed ?? ownList;
  const base = healed ?? ownList;

  // Sort on the SHARED-STORE key when both sides have one, falling back to the
  // timestamp otherwise.
  //
  // INCIDENT 2026-08-06: this used to sort by timestamp alone and rely on the
  // sort being stable to preserve "question above answer". That reasoning was
  // wrong in production. The server writes both docs of a turn with the SAME
  // orderTs and distinguishes them ONLY by `seq`, and several lanes stamp both
  // with the same `timestamp` too — so the pair's order fell through to
  // insertion order, which differs per viewer (the author's own question sits in
  // `ownList`, another participant's sits in `additions`). Other participants
  // therefore saw Prest0n's reply ABOVE the message it answered, which reads as
  // Prest0n replying twice.
  //
  // Comparing orderTs→seq first makes the rendered order the same one the server
  // established, identically for every viewer.
  return [...base, ...additions].sort((a, b) => compareForTranscript(a, b));
}
